import type { WeightSet } from './types';
import { NEUTRAL_WEIGHTS } from './types';

/**
 * Higher score first; equal scores keep the order the caller produced when
 * `compareScoreDesc` returns 0 (stable sort). Prefer `compareScoreDescThenId`
 * when a deterministic id tie-break is required (sold-corpus picks).
 */
export const compareScoreDesc = (
  a: { readonly score: number },
  b: { readonly score: number }
): number => b.score - a.score;

/**
 * Deterministic rank comparator: higher score first, then stable product id.
 * Prevents non-deterministic picks when scores tie (no Math.random).
 */
export const compareScoreDescThenId = (
  a: { score: number; id: string },
  b: { score: number; id: string }
): number => {
  if (b.score !== a.score) return b.score - a.score;
  return a.id.localeCompare(b.id);
};

type Rankable = { readonly id: string; readonly score: number };

/**
 * Order-only ranker.
 *
 * INVARIANT: returns a permutation of the input — same ids, same count.
 * `_weights` is accepted so doors can pass org config without forking the
 * call shape; scoring that consumes weights happens *before* rank (see
 * `recommendProducts`). A weight of zero mutes a signal upstream; no weight
 * can admit a record the caller did not pass.
 */
export const rank = <T extends Rankable>(
  candidates: readonly T[],
  _weights: WeightSet = NEUTRAL_WEIGHTS
): readonly T[] =>
  // A copy, not an in-place sort: the caller's array must not be mutated.
  [...candidates].sort(compareScoreDescThenId);

/**
 * Assert the order-only invariant (for tests and calibration harnesses).
 */
export const sameMembership = (
  before: readonly { readonly id: string }[],
  after: readonly { readonly id: string }[]
): boolean => {
  if (before.length !== after.length) return false;
  const a = new Set(before.map((row) => row.id));
  const b = new Set(after.map((row) => row.id));
  if (a.size !== b.size) return false;
  for (const id of a) if (!b.has(id)) return false;
  return true;
};
